import { TEXT_BODY, TEXT_MUTED, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { SORCERY_CHART_COLORS } from '../../utils/sorcery/deckMetrics';
import StatCard from './StatCard';
import { noiseBarStyle } from './ChartNoise';
import { t } from '../../localization/t';

const TYPE_COLORS = {
  Minion: SORCERY_CHART_COLORS.Earth,
  Magic: SORCERY_CHART_COLORS.Fire,
  Aura: SORCERY_CHART_COLORS.Air,
  Artifact: SORCERY_CHART_COLORS.Neutral,
};

function StackedTypeBar({ data }) {
  const total = data.reduce((sum, row) => sum + row.value, 0);
  if (total === 0) {
    return (
      <div className="text-sm" style={{ color: TEXT_BODY, opacity: 0.7 }}>
        {t('No cards yet.')}
      </div>
    );
  }
  return (
    <div>
      <div className="flex h-7 rounded-md overflow-hidden" style={{ boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.18)' }}>
        {data.map((row) => {
          const pct = (row.value / total) * 100;
          return (
            <div
              key={row.label}
              style={{
                width: `${pct}%`,
                ...noiseBarStyle(`linear-gradient(180deg, ${TYPE_COLORS[row.label] || row.fill} 0%, ${TYPE_COLORS[row.label] || row.fill}cc 100%)`),
              }}
              title={t('{label}: {value}', { label: t(row.label), value: row.value })}
            />
          );
        })}
      </div>
      <div className="grid grid-cols-2 gap-y-1 gap-x-3 mt-3 text-xs">
        {data.map((row) => {
          const pct = total === 0 ? 0 : Math.round((row.value / total) * 100);
          return (
            <div key={row.label} className="flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-sm" style={{ background: TYPE_COLORS[row.label] || row.fill }} />
              <span className="flex-1" style={{ color: TEXT_BODY }}>{t(row.label)}</span>
              <span className="tabular-nums" style={{ color: TEXT_PRIMARY }}>{row.value}</span>
              <span className="tabular-nums w-10 text-right" style={{ color: TEXT_MUTED }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SubtypeList({ data, minionCount }) {
  if (!data || data.length === 0) {
    return (
      <div className="text-sm" style={{ color: TEXT_BODY, opacity: 0.7 }}>
        {t('No tribal concentration.')}
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="flex flex-col gap-2">
      {data.map((row) => {
        const pct = minionCount === 0 ? 0 : Math.round((row.value / minionCount) * 100);
        const width = (row.value / max) * 100;
        return (
          <div key={row.label} className="flex items-center gap-2 text-xs">
            <span className="w-20 truncate" style={{ color: TEXT_PRIMARY }}>{row.label}</span>
            <div className="flex-1 h-2.5 rounded-full" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(180,140,60,0.1)' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${width}%`,
                  ...noiseBarStyle(`linear-gradient(90deg, ${SORCERY_CHART_COLORS.Neutral}dd 0%, ${SORCERY_CHART_COLORS.Neutral}88 100%)`),
                }}
              />
            </div>
            <span className="w-6 text-right tabular-nums" style={{ color: TEXT_BODY }}>{row.value}</span>
            <span className="w-10 text-right tabular-nums" style={{ color: TEXT_MUTED }}>{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function KeywordList({ keywords, spellbookSize }) {
  if (!keywords || keywords.length === 0) {
    return (
      <div className="text-sm" style={{ color: TEXT_BODY, opacity: 0.7 }}>
        {t('No tracked keywords in this deck.')}
      </div>
    );
  }
  // Bar widths represent how much of the whole spellbook carries each
  // keyword (absolute saturation), not relative to each other. So a
  // 4-of-60 Airborne reads as "thin bar, 7% of the deck" regardless of
  // what the other keywords look like.
  return (
    <div className="flex flex-col gap-2 text-xs">
      {keywords.map((kw) => {
        const ratio = spellbookSize === 0 ? 0 : kw.count / spellbookSize;
        const pct = Math.round(ratio * 100);
        const barPct = ratio * 100;
        return (
          <div key={kw.key} className="flex items-center gap-2">
            <span className="w-20 truncate" style={{ color: TEXT_PRIMARY }} title={kw.description}>
              {kw.label}
            </span>
            <div
              className="flex-1 h-2.5 rounded-full min-w-0"
              style={{
                background: 'rgba(0,0,0,0.4)',
                border: '1px solid rgba(180,140,60,0.1)',
              }}
            >
              <div
                className="h-full rounded-full"
                style={{
                  width: `${barPct}%`,
                  ...noiseBarStyle(`linear-gradient(90deg, ${SORCERY_CHART_COLORS.Neutral}dd 0%, ${SORCERY_CHART_COLORS.Neutral}88 100%)`),
                }}
              />
            </div>
            <span className="w-6 text-right tabular-nums" style={{ color: TEXT_BODY }}>{kw.count}</span>
            <span className="w-10 text-right tabular-nums" style={{ color: TEXT_MUTED }}>{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

export function CardTypesCard({ metrics, className }) {
  return (
    <StatCard title={t('Card Types')} description={t('Spellbook composition by card category.')} className={className}>
      <StackedTypeBar data={metrics.charts.types} />
    </StatCard>
  );
}

export function TribesCard({ metrics, className }) {
  return (
    <StatCard title={t('Tribes')} description={t('Distribution of minion subtypes.')} className={className}>
      <SubtypeList data={metrics.charts.subtypes} minionCount={metrics.totals.minionCount} />
    </StatCard>
  );
}

export function KeywordSignalsCard({ metrics, className }) {
  return (
    <StatCard title={t('Keyword Signals')} className={className}>
      <KeywordList keywords={metrics.keywords} spellbookSize={metrics.totals.spellbookSize} />
    </StatCard>
  );
}
